import { withNamespaces } from "react-i18next"
import Accordion from 'react-bootstrap/Accordion';
import { Badge, Card, CardBody, Col, Row, Table, UncontrolledTooltip } from "reactstrap";
import { buildNestedObject } from "../../helpers";
import { RiAlertLine, RiBarChart2Line, RiChatQuoteLine, RiInformationLine, RiLineChartLine, RiPercentLine, RiScales3Line, RiShieldStarLine } from "react-icons/ri";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import { ActivityExposureOptions } from "../../constants";

const formatCurrency = (value, currency = "EUR") => {
    if (value == null) return "-";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
    }).format(value);
};

const CreditAssessment = ({
    t,
    activeAccordion,
    setActiveAccordion,

    detailsQuery
}) => {
    if (detailsQuery.isFetching) {
        return (
            <Row>
                <Col 
                    sm="12" 
                    className="mt-4 mb-4"
                >
                    <div 
                        className="dt-field dt-skeleton" 
                        style={{ width: '100%', height: '100px' }}
                    />
                </Col>
            </Row>
        )
    }

    const dateUtils = new DateUtils();

    const organizationData = JSON.parse(detailsQuery.data?.company?.organizationData || "");

    const generic = buildNestedObject(organizationData?.additional_information?.generic || []);

    const creditScore = organizationData?.credit_score || {};

    return (
        <Accordion.Item
            onClick={() => setActiveAccordion('credit-assessment')}
            className={activeAccordion === 'credit-assessment' ? 'active' : ''}
            eventKey={'credit-assessment'}
        >
            <Accordion.Header>
                <RiShieldStarLine size="24" className="me-2" />

                <span>
                    {t('Risk & Credit Assessment')}
                </span>
            </Accordion.Header>

            <Accordion.Body>
                {/* Credit Score */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiPercentLine size="24" />
                            </span>
                            <div className="fw-bold font-size-16">
                                {t('Credit Score')}
                            </div>
                        </div>

                        <Row>
                            <Col sm="6">
                                <div className="mb-2">
                                    <strong>{t('Current Credit Rating')}:</strong>

                                    <div className="mt-2 mb-1">
                                        {t('Provider Value')}: {creditScore.current_credit_rating.provider_value?.value} {`(${t('Min')}: ${creditScore.current_credit_rating.provider_value?.min_value}, ${t('Max')}: ${creditScore.current_credit_rating.provider_value?.max_value})`}
                                    </div>
                                    
                                    <div className="mb-1">
                                        {t('Common Value')}: {creditScore.current_credit_rating?.common_value} ({creditScore.current_credit_rating?.common_description})
                                    </div>
                                    
                                    <div className="mb-1">
                                        {t('Credit Limit')}: {formatCurrency(creditScore.current_credit_rating?.credit_limit)}
                                    </div>
                                </div>
                            </Col>

                            <Col sm="6">
                                <div>
                                    <strong>{t('Previous Credit Rating')}:</strong>

                                    <div className="mt-2 mb-1">
                                        {t('Provider Value')}: {creditScore.previous_credit_rating.provider_value?.value} {`(${t('Min')}: ${creditScore.previous_credit_rating.provider_value?.min_value}, ${t('Max')}: ${creditScore.previous_credit_rating.provider_value?.max_value})`}
                                    </div>
                                    
                                    <div className="mb-1">
                                        {t('Common Value')}: {creditScore.previous_credit_rating?.common_value} ({creditScore.previous_credit_rating?.common_description})
                                    </div>
                                    
                                    <div className="mb-1">
                                        {t('Credit Limit')}: {formatCurrency(creditScore.previous_credit_rating?.credit_limit)}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Exposure Indicator */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiAlertLine size="24" />
                            </span>
                            <div className="fw-bold font-size-16">
                                {t('Exposure Indicator')}
                            </div>
                        </div>

                        <Row>
                            <Col sm="6" className="mb-2">
                                <div>
                                    <strong>
                                        {t('Company Exposure')}{' '}
                                        <span
                                            id={`tooltip-company-exposure`}
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target={`tooltip-company-exposure`}
                                        >
                                            {t('Represents the overall company exposure score. Lower values indicate lower financial or operational risk.')}
                                        </UncontrolledTooltip>
                                    </strong>{': '}
                                    {generic.exposureIndicator?.companyExposure}
                                </div>
                            </Col>

                            <Col sm="6" className="mb-2">
                                <div>
                                    <strong>
                                        {t('Activity Exposure')}{' '}
                                        <span
                                            id={`tooltip-activity-exposure`}
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target={`tooltip-activity-exposure`}
                                        >
                                            {t('Indicates the risk level associated with the company’s primary activity. B = Medium risk.')}
                                        </UncontrolledTooltip>
                                    </strong>{': '}

                                    {`${generic.exposureIndicator?.activityExposure} (${ActivityExposureOptions[generic.exposureIndicator?.activityExposure]})`}
                                </div>
                            </Col>

                            <Col sm="6" className="mb-2">
                                <div><strong>{t('Activity Sector')}:</strong> {generic.exposureIndicator?.activitySector}</div>
                            </Col>

                            <Col sm="6" className="mb-2">
                                <div>
                                    <strong>
                                        {t('Activity Loss')}{' '}
                                        <span
                                            id={`tooltip-activity-loss`}
                                            style={{ cursor: "pointer" }}>
                                            <RiInformationLine />
                                        </span>
                                        <UncontrolledTooltip
                                            target={`tooltip-activity-loss`}
                                        >
                                            {t('Indicates historical loss associated with the company’s main activity. Typically measured in currency or as an index.')}
                                        </UncontrolledTooltip>
                                    </strong>{': '}
                                    {generic.exposureIndicator?.activityLoss}
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Rating History */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiBarChart2Line size="24" />
                            </span>
                            <div className="fw-bold font-size-16">
                                {t('Rating History')}
                            </div>
                        </div>

                        <Table striped bordered>
                            <thead>
                                <tr>
                                    <th>{t('Date')}</th>
                                    <th>{t('Local Rating')}</th>
                                    <th>{t('Common Value')}</th>
                                    <th>{t('Description')}</th>
                                    <th>{t('Credit Limit')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {generic.ratingHistory?.map((r, i) => (
                                    <tr key={i}>
                                        <td>{dateUtils.convertDateToDate(r.date, INTERNATIONAL_DATE_FORMAT)}</td>
                                        <td
                                            className="text-end"
                                        >{r.localRating}</td>
                                        <td>{r.commonRating.commonValue}</td>
                                        <td>{r.commonRating.commonDescription} - {r.description2}</td>
                                        <td
                                            className="text-end"
                                        >
                                            {formatCurrency(r.limit)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Industry Averages */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiScales3Line size="24" />
                            </span>
                            <div className="fw-bold font-size-16">
                                {t('Industry Averages')}
                            </div>
                        </div>

                        <Row>
                            <Col sm="6" className="mb-2">
                                <strong>
                                    {t('Credit Limit')}{' '}
                                    <span
                                        id={`tooltip-industry-averages-credit-limit`}
                                        style={{ cursor: "pointer" }}>
                                        <RiInformationLine />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-industry-averages-credit-limit`}
                                    >
                                        {t('Typical credit limit for companies in this industry, based on aggregated data from similar businesses.')}
                                    </UncontrolledTooltip>
                                </strong>{': '}
                                {formatCurrency(generic.industryAverages?.creditLimit)}
                            </Col>

                            <Col sm="6" className="mb-2">
                                <strong>
                                    {t('Credit Rating')}{' '}
                                    <span
                                        id={`tooltip-industry-averages-credit-rating`}
                                        style={{ cursor: "pointer" }}>
                                        <RiInformationLine />
                                    </span>
                                    <UncontrolledTooltip
                                        target={`tooltip-industry-averages-credit-rating`}
                                    >
                                        {t('Average credit rating for companies in this industry, represented as a numeric score. Higher values indicate stronger creditworthiness relative to industry peers.')}
                                    </UncontrolledTooltip>
                                </strong>{': '}
                                {generic.industryAverages?.creditRating}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Industry Comparison */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiLineChartLine size="24" />
                            </span>
                            <div className="fw-bold font-size-16">
                                {t('Industry Comparison')}
                            </div>
                        </div>

                        <Row>
                            <Col sm="6" className="mb-2">
                                <strong>{t('Activity Code')}:</strong>{' '}
                                {generic.industryComparison?.activityCode}
                            </Col>

                            <Col sm="6" className="mb-2">
                                <strong>{t('Activity Description')}:</strong>{' '}
                                {generic.industryComparison?.activityDescription}
                            </Col>

                            <Col sm="6" className="mb-2">
                                <strong>{t('Industry Avg Payment Expectation Days')}:</strong> {generic.industryComparison?.industryAveragePaymentExpectationDays}
                            </Col>

                            <Col sm="6" className="mb-2">
                                <strong>{t('Industry Avg Day Sales Outstanding')}:</strong> {generic.industryComparison?.industryAverageDaySalesOutstanding}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Commentaries */}
                <Card className="h-100 h-100 shadow-sm mb-4">
                    <CardBody className="p-1">
                        <div className="d-flex align-items-center mb-2">
                            <span className="me-2 text-primary">
                                <RiChatQuoteLine size="24" />
                            </span>
                            <div className="fw-bold font-size-16">
                                {t('Commentaries')}
                            </div>
                        </div>

                        {generic.commentaries?.map((c, i) => (
                            <div key={i} className="mb-2">
                                <Badge
                                    className={`me-2 bg-${c.positiveOrNegative === "Positive" ? "success" : c.positiveOrNegative === "Negative" ? "danger" : "secondary"}`}
                                >
                                    {c.positiveOrNegative}
                                </Badge>
                                <span>{c.commentaryText}</span>
                            </div>
                        ))}

                    </CardBody>
                </Card>
            </Accordion.Body>
        </Accordion.Item>
    )
}

export default withNamespaces()(
    CreditAssessment
)